import React, { FC, ReactNode, useMemo } from 'react';
import { HoverEvents, useHover, usePress } from 'react-aria';

import { SkinColor } from '../../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { TestProps } from '../../../internal/types/testTypes';
import { useVStack } from '../../../hooks/useStack';
import { FloatingButton } from '../../FloatingButton';
import { StackAlignItems } from '../../Stack';
import { IconPlus } from '../../../icons';
import { MediaControl } from '../MediaControl';
import { cn } from '../../../lib/css/className/className';

import './MediaItemBase.css';

type MediaItemBaseColor = 'normal' | 'contrast';

const cls = cn('MediaItemBase');

/**
 * У обертки для картинки должно быть либо явно задано содержимое -
 * тогда размер диктуется содержимым,
 * либо ширина и высота - для пустого состояния обёртки
 * */
const skinMap: Record<MediaItemBaseColor, SkinColor> = {
    contrast: 'gray-low-contrast',
    normal: 'gray-neutral',
};

type MediaItemBaseContentProps = {
    /**
     * Ширина элемента
     */
    width?: string | number;
    /**
     * Высота элемента
     */
    height?: string | number;
    /**
     * Компонент картинки
     */
    media?: ReactNode;
};

export type MediaItemBaseProps = TestProps &
    MediaItemBaseContentProps &
    HoverEvents & {
        /**
         * Названия классов для элемента.
         */
        className?: string;
        /**
         * Выравнивание содержимого по горизонтали
         */
        alignItems?: StackAlignItems;
        /**
         * Объекты контролов для картинки
         * `<MediaControl .../>`
         *
         * Можно указать несколько обернув в Fragment`<></>`
         */
        controls: ReactNode;

        /**
         * Фон MediaItem, который виден если MediaItem пустое или
         * картинка не вписывается в размеры и не имеет собственного фона
         * @defaultValue normal
         */
        color?: MediaItemBaseColor;

        /** медиа-контент блока находится в процессе загрузки */
        isLoading?: boolean;

        /** медиа-контент блока находится в выбранном состоянии */
        isActive?: boolean;

        /** MediaItem имеет подсветку при наведении */
        isHoverable?: boolean;

        /** MediaItem задисейблен */
        isDisabled?: boolean;

        /** MediaItem показывает IconPlus когда нет media */
        isAddable?: boolean;

        /** Обработчик клика по MediaItem  */
        onClick?(): void;

        /**
         * Скругление изображения в пикселях.
         * Размеры определены дизайн-системой
         */
        borderRadius?: '8' | '10' | '12' | '14';

        /**
         * Компонент названия-описания
         */
        titleNode?: ReactNode;

        /**
         * Кастомный контент, отображаемый под основным содержимым карточки.
         * Потребитель самостоятельно определяет, что находится внутри блока.
         */
        customContent?: ReactNode;
    };

export const MediaItemBase: FC<MediaItemBaseProps> = function MediaItemBase(props) {
    const {
        'data-testid': dataTestId = 'MediaItemBase',

        className,
        controls,
        alignItems = 'flex-start',
        titleNode,
        customContent,
        color = 'normal',
        isHoverable = true,
        isAddable = true,
        media,
        isLoading,
        width,
        height,
        isDisabled,
        onClick,
        onHoverChange,
        onHoverStart,
        onHoverEnd,
        borderRadius,
        isActive = false,
    } = props;

    const { pressProps } = usePress({
        onPress: onClick,
    });

    const { isHovered: isHoveredIn, hoverProps } = useHover({ onHoverChange, onHoverStart, onHoverEnd });

    const isHovered = useMemo(() => Boolean(isHoverable ? isHoveredIn : false), [isHoveredIn, isHoverable]);

    const skinClass = getColorSkin(skinMap[color], { isHovered, isDisabled });

    const sizeStyle = useMemo(() => {
        return width && height
            ? {
                  width,
                  height,
              }
            : undefined;
    }, [width, height]);

    const hasNoMedia = !media;

    const stackStyles = useVStack({ alignItems, gap: '8', inline: true });
    const contentStackStyles = useVStack({ alignItems: 'center', justifyContent: 'center' });
    const controlsNode = useMemo(() => {
        if (isLoading) {
            return (
                <MediaControl isAlwaysVisible position="center">
                    <FloatingButton isLoading size="l" color="contrast" />
                </MediaControl>
            );
        }

        if (hasNoMedia && isAddable) {
            return (
                <MediaControl isAlwaysVisible position="center">
                    <IconPlus size="20" color={'gray'} />
                </MediaControl>
            );
        }

        return controls;
    }, [controls, isLoading, hasNoMedia, isAddable]);

    return (
        <div
            style={sizeStyle}
            data-testid={dataTestId}
            className={cls({ hoverable: isHoverable, empty: hasNoMedia }, [className, stackStyles])}
            {...(onClick ? pressProps : {})}
            {...hoverProps}
        >
            <div
                data-testid={`${dataTestId}.Content`}
                className={cls(
                    'content',
                    {
                        hovered: isHovered,
                        active: isActive,
                        borderRadius: borderRadius,
                        hasBorderRadius: Boolean(borderRadius),
                        noMedia: hasNoMedia,
                    },
                    [className, skinClass, contentStackStyles],
                )}
            >
                {media}
                {controlsNode}
                {isLoading && <div className={cls('overlay')} />}
            </div>
            {titleNode && (
                <div data-testid={`${dataTestId}.TitleNode`} className={cls('title')}>
                    {titleNode}
                </div>
            )}
            {customContent && (
                <div data-testid={`${dataTestId}.CustomContent`} className={cls('customContent')}>
                    {customContent}
                </div>
            )}
        </div>
    );
};
